import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import SuccessIconNoBackground from '@images/svg-icons/successIconNoBackground.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import {
    PersonalDetails,
    PersonalInformation,
    PrimaryFormFields,
    PrimaryFormFieldsSettings,
    SecondaryFormFields,
    ThirdFormFields,
    UpdatePersonalDetailsPayload,
    UpdatePersonalInformationPayload,
} from '@interfaces/myProfileInterfaces';

import PersonalDetailsPrimaryForm from './PersonalDetailsPrimaryForm';
import PersonalDetailsSecondaryForm from './PersonalDetailsSecondaryForm';
import PersonalDetailsThirdForm from './PersonalDetailsThirdForm';

import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';

import { updatePersonalDetails } from '@services/myProfileServices';

import { convertDate, getLabelFallback, getPartialPhoneNumber } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import { isOfTypeGender, isValidZipCode } from '@utils/pages-utils/MyProfileUtils';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { isValid, differenceInYears, compareAsc } from 'date-fns';
import { getStringFromDate } from '@utils/dateUtils';
import { LOGON_ID_STORE } from '@utils/const';
import { useEffect, useState } from 'react';

interface Props {
    personalDetails: PersonalInformation;
}

const PersonalDetailsForm = ({ personalDetails }: Props) => {
    const config = useSelector((state: RootState) => state.common.config);
    const updatePersonalDetailsRequestStatus = useSelector(
        (state: RootState) => state.myProfile.updatePersonalDetailsRequestStatus
    );
    const [chekedCheckBox, setChekedCheckBox] = useState<string>(null);

    useEffect(() => {
        if (!!personalDetails) {
            setChekedCheckBox(isOfTypeGender(personalDetails?.gender) ? personalDetails?.gender : '')
        }
    }, [personalDetails])

    const dispatch = useDispatch();

    const schema = yup.object().shape({
        firstName: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGFIRSTNAME_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_BILLINGFIRSTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        lastName: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGLASTNAME_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_BILLINGLASTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        email: yup
            .string()
            .email(getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_EMAIL_MESSAGE'))
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_EMAIL_LABEL')
                )
            ),
        isDefault: yup.boolean(),
        phoneNumber1: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_PHONENUMBER1_LABEL')
                )
            )
            .test(
                'len',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_PHONENUMBER1'),
                val => (val ? val.replace(/\D/g, '').length >= 10 : true)
            ),
        dob: yup
            .string()
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'),
                value => {
                    return isValid(new Date(value)) &&
                        compareAsc(new Date(value), new Date('01/01/1900')) !== -1 &&
                        compareAsc(new Date(value), new Date()) !== 1 &&
                        value.replace(/\//g, '').length === 8
                        ? true
                        : false;
                }
            )
            .test(
                'min',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_OF_BIRTH_MESSAGE'),
                value => !(differenceInYears(new Date(), new Date(value)) < 18)
            )

            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_BIRTHDATE_LABEL')
                )
            )
            .typeError(getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'))

            .nullable(),
        gender: yup.string(),
    });

    const defaultFormValues: PrimaryFormFieldsSettings & SecondaryFormFields & ThirdFormFields = {
        firstName: personalDetails?.firstname ?? '',
        lastName: personalDetails?.lastname ?? '',
        email: personalDetails?.email ?? '',
        phoneNumber1: personalDetails?.phone1 ?? '',
        gender: isOfTypeGender(personalDetails?.gender) ? personalDetails?.gender : '',
        dob: personalDetails?.dob
            ? personalDetails.dob
            : '',
    };

    const methods = useForm<PrimaryFormFields | SecondaryFormFields | ThirdFormFields>({
        resolver: yupResolver(schema),
        defaultValues: defaultFormValues,
    });

    const onSubmit: SubmitHandler<PrimaryFormFieldsSettings & SecondaryFormFields & ThirdFormFields> = (
        data: PrimaryFormFieldsSettings & SecondaryFormFields & ThirdFormFields
    ) => {
        const updatePersonalInformationPayload: UpdatePersonalInformationPayload = {
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            dateOfBirth: convertDate(data.dob),
            logonId: LOGON_ID_STORE + data.email,
            userProfileField2: (data.phoneNumber1).replace('(+1) ','').replace(/\-/g, ""),
            demographicField5: data.lastName,
            demographicField7: data.firstName,
            gender: chekedCheckBox,//data.gender,
        };
    
        dispatch(updatePersonalDetails(updatePersonalInformationPayload));
    };

    return (
        <div role="region" aria-label="Personal details form" className="personal-details">
            <div className="personal-details-forms-container content-container">
                <span className="secondary-title mb-24">
                    {getLabelFallback('SETTINGS_PERSONAL_INFORMATION_TITLE')}
                </span>
                <FormProvider {...methods}>
                    <form autoComplete="on" onSubmit={methods.handleSubmit(onSubmit)}>
                        <PersonalDetailsPrimaryForm defaultFormValues={defaultFormValues} />
                        <PersonalDetailsSecondaryForm />
                        <PersonalDetailsThirdForm setChekedCheckBox={setChekedCheckBox} chekedCheckBox={chekedCheckBox} />
                        <div className="form-footer">
                            <button
                                data-element-id="PersonalDetails_Save"
                                className={`primary-button button-size-medium ${updatePersonalDetailsRequestStatus === 'LOADING'
                                        ? 'loading'
                                        : ''
                                    }`}
                                type="submit"
                            >
                                {getLabelFallback(
                                    'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SAVE_CHANGES_CTA'
                                )}
                                {updatePersonalDetailsRequestStatus === 'LOADING' && <PreLoader />}
                            </button>

                            {updatePersonalDetailsRequestStatus === 'SUCCESS' && (
                                <div className="success-message">
                                    <SuccessIconNoBackground />
                                    <span className="secondary-message medium success bold">
                                        {getLabelFallback(
                                            'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SUCCESS_MESSAGE'
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default PersonalDetailsForm;
